import { parseDayKey } from "../utils";
import {
  agentHomeworkBlock,
  extractProblemFromRows,
  okrionHomeworkBlock,
  shortProblem,
} from "./scheduleConnect";

export function isHomeworkActivity(activity = "") {
  return /homework|tonight/i.test(String(activity || ""));
}

function isHolidayRow(row) {
  return /holiday|vacation|study holiday/i.test(String(row?.[2] || ""));
}

function textOf(row) {
  return String(row?.[3] ?? "").replace(/\\n/g, "\n").trim();
}

/** Only the first homework format (typed LeetCode / Agent / Case rows) counts as “has body”. */
export function looksLikeOriginalHomework(content = "") {
  return /^(LeetCode|Exercism|LinkedIn|Agent Workbench|Case study)\b/im.test(
    String(content || "").replace(/\\n/g, "\n")
  );
}

function isRepeatedTemplate(content = "") {
  const t = String(content || "");
  if (/Event Logger Agent \(Easy\)(?!\s*—\s*Day)/.test(t)) return true;
  if (/Easy drill on/i.test(t)) return true;
  return looksLikeOriginalHomework(t) && !/Problem \(short\)/i.test(t);
}

export function homeworkHasBody(rows = []) {
  return normalizePlanRows(rows).some(
    (r) =>
      isHomeworkActivity(r[2]) &&
      looksLikeOriginalHomework(textOf(r)) &&
      !isRepeatedTemplate(textOf(r))
  );
}

export function normalizePlanRows(rows = []) {
  if (!Array.isArray(rows)) return [];
  return rows
    .map((r) => {
      if (Array.isArray(r) && r.length >= 3) {
        const content = Array.isArray(r[3]) ? r[3].filter(Boolean).join("\n") : r[3];
        return [r[0], r[1], r[2], content];
      }
      if (r && typeof r === "object") {
        return [
          r.dayId || r.dayKey || r.day || "",
          r.time || "",
          r.activity || r.title || "",
          Array.isArray(r.content) ? r.content.filter(Boolean).join("\n") : r.content || r.task || "",
        ];
      }
      return null;
    })
    .filter(Boolean);
}

function labeledValue(content, keys) {
  const raw = String(content || "").replace(/\\n/g, "\n");
  for (const key of keys) {
    const re = new RegExp(`(?:^|\\n)\\s*(?:▶\\s*)?${key}\\s*:\\s*([^\\n]+)`, "i");
    const m = raw.match(re);
    if (m && m[1].trim()) return m[1].trim().replace(/^["']|["']$/g, "");
  }
  return "";
}

function firstUsefulLine(content = "") {
  const lines = String(content || "")
    .replace(/\\n/g, "\n")
    .split("\n")
    .map((l) => l.replace(/^▶\s*/, "").replace(/^[·•]\s*/, "").trim())
    .filter(Boolean);
  for (const line of lines) {
    if (/^https?:\/\//i.test(line)) continue;
    if (/^(why|outcome|links?|done when|youtube|article|watch)\s*:/i.test(line)) continue;
    if (line.length < 4) continue;
    return line.replace(/^(?:heading|learn today|subject|topic)\s*:\s*/i, "").slice(0, 160);
  }
  return "";
}

function pickFromRows(dayRows, titleRe, labelKeys) {
  const hit = (dayRows || []).find((r) => titleRe.test(String(r[2] || "")));
  if (!hit) return { title: "", topic: "", content: "" };
  const topic =
    labeledValue(hit[3], labelKeys) ||
    firstUsefulLine(hit[3]) ||
    String(hit[2] || "").trim();
  return { title: String(hit[2] || "").trim(), topic, content: textOf(hit) };
}

function clip(s, n) {
  const t = String(s || "").replace(/\s+/g, " ").trim();
  if (!t) return "";
  return t.length > n ? `${t.slice(0, n).trim()}…` : t;
}

function isGenericTopic(s = "") {
  return /^(today'?s |your project|learning|project build|system design|stand-?up|tonight)/i.test(
    String(s || "").trim()
  );
}

function pickNonGeneric(...vals) {
  return vals.map((v) => clip(v, 90)).find((v) => v && !isGenericTopic(v)) || "";
}

function extractCodingLc(content = "") {
  const c = String(content || "").replace(/\\n/g, "\n");
  const hit =
    c.match(/LeetCode\s*#\s*(\d+)\s*[—–-]\s*([^\n(]+)/i) ||
    c.match(/#\s*(\d+)\s*[—–-]\s*([^\n(]+)/);
  const url = (c.match(/https?:\/\/leetcode\.com\/problems\/[\w-]+\/?/i) || [])[0] || "";
  const pat = c.match(/Pattern[:\s]+([^\n]+)/i)?.[1] || "";
  if (!hit && !url) return { lc: "", name: "", url: "", pattern: clip(pat, 50) };
  return {
    lc: hit ? hit[1] : "",
    name: hit ? String(hit[2] || "").trim() : "",
    url: url.replace(/[.,;:!?)]+$/g, ""),
    pattern: clip(pat, 50),
  };
}

const LC_ROTATION = [
  { lc: "1", name: "Two Sum", slug: "two-sum", pattern: "Arrays & Hashing" },
  { lc: "20", name: "Valid Parentheses", slug: "valid-parentheses", pattern: "Stack" },
  { lc: "21", name: "Merge Two Sorted Lists", slug: "merge-two-sorted-lists", pattern: "Linked List" },
  { lc: "121", name: "Best Time to Buy and Sell Stock", slug: "best-time-to-buy-and-sell-stock", pattern: "Arrays" },
  { lc: "125", name: "Valid Palindrome", slug: "valid-palindrome", pattern: "Two Pointers" },
  { lc: "141", name: "Linked List Cycle", slug: "linked-list-cycle", pattern: "Linked List" },
  { lc: "206", name: "Reverse Linked List", slug: "reverse-linked-list", pattern: "Linked List" },
  { lc: "217", name: "Contains Duplicate", slug: "contains-duplicate", pattern: "Arrays & Hashing" },
  { lc: "242", name: "Valid Anagram", slug: "valid-anagram", pattern: "Hash Map" },
  { lc: "704", name: "Binary Search", slug: "binary-search", pattern: "Binary Search" },
  { lc: "733", name: "Flood Fill", slug: "flood-fill", pattern: "BFS / DFS" },
  { lc: "876", name: "Middle of the Linked List", slug: "middle-of-the-linked-list", pattern: "Two Pointers" },
];

const CASE_ROTATION = [
  { title: "Airbnb — host trust after a bad stay", theme: "Trust / marketplace", what: "A guest had a poor stay; hosts and Airbnb must rebuild trust without killing supply." },
  { title: "Netflix — homepage feels random", theme: "Recommendations", what: "Users skip the first row; the product must show “for you” without more noise." },
  { title: "Uber — driver / rider mismatch at peak", theme: "Matching", what: "Peak hour: riders wait, drivers sit idle a few blocks away." },
  { title: "Slack — notification overload", theme: "Attention", what: "Teams mute Slack because every ping feels urgent." },
  { title: "Spotify — Discover Weekly went stale", theme: "Personalization", what: "The weekly mix repeats last month's songs." },
  { title: "Amazon — returns feel harder than buying", theme: "Ops / UX", what: "Customers start checkout then abandon because return policy is unclear." },
  { title: "Zoom — meeting fatigue", theme: "Product habit", what: "People skip optional calls; useful stand-ups get skipped too." },
  { title: "Instagram — comment spam", theme: "Moderation", what: "Creators lose trust when spam replies bury real comments." },
  { title: "Stripe — failed payment recovery", theme: "Payments", what: "A card fails; the user never gets a clear retry path." },
  { title: "Notion — workspace chaos", theme: "Information architecture", what: "Teams cannot find the one page that is the source of truth." },
];

function pickRot(list, dayIdx) {
  return list[Math.abs(Number(dayIdx) || 0) % list.length];
}

function dayMeta(dayKey) {
  const names = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];
  const p = parseDayKey(dayKey) || {};
  const week = Number(p.week) || 1;
  const di = names.findIndex((n) => n.toLowerCase() === String(p.dayName || "").toLowerCase());
  const dow = di >= 0 ? di : 0;
  const dayIdx = Math.max(0, (week - 1) * 5 + dow);
  return { week, dow, dayIdx, dayName: names[dow] };
}

function nextSpeakDay(dayKey) {
  const names = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];
  const p = parseDayKey(dayKey) || {};
  const week = Number(p.week) || 1;
  const idx = names.findIndex((n) => n.toLowerCase() === String(p.dayName || "").toLowerCase());
  if (idx < 0) return { dayName: "Tuesday", week };
  if (idx >= 4) return { dayName: "Monday", week: week + 1 };
  return { dayName: names[idx + 1], week };
}

export function topicsFromDayRows(dayRows = []) {
  const work = normalizePlanRows(dayRows).filter((r) => !isHomeworkActivity(r[2]) && !isHolidayRow(r));
  const learn = pickFromRows(
    work,
    /learning|concept|theory|subject/i,
    ["What to Learn", "Concept", "Learned today", "TODAY", "Heading", "Learn today", "Subject", "Topic"]
  );
  const sd = pickFromRows(
    work,
    /system design|architecture|hld|lld/i,
    ["What to Learn", "Concept", "DT Stage", "Real Project Task", "Do this", "Deliverable"]
  );
  const project = pickFromRows(
    work,
    /project|product|dt playbook|mini build|agent builder/i,
    ["Real Project Task", "Deliverable", "Do this", "What to Learn", "Concept", "Built today"]
  );
  const coding = pickFromRows(
    work,
    /coding|dsa|leetcode|problem solving/i,
    ["Concept", "Pattern", "What to Learn", "Topic"]
  );
  const problemRow = pickFromRows(
    work,
    /problem review|problem lab|problem statement/i,
    ["Problem", "Concept", "What to Learn", "Heading"]
  );
  const topic =
    pickNonGeneric(learn.topic, coding.topic, project.topic, sd.topic) || "today's class topic";
  const build = pickNonGeneric(project.topic, sd.topic) || "today's tool slice";
  const design = pickNonGeneric(sd.topic) || "today's system-design box";
  const product = pickNonGeneric(problemRow.topic, project.topic, topic) || "your project";
  const lc = extractCodingLc(coding.content);
  const problem = extractProblemFromRows(work) || product;
  return { topic, build, design, product, lc, coding, dayKey: work[0]?.[0] || "", problem };
}

export function buildTonightHomework(dayRows = [], dayKeyHint = "") {
  const extracted = topicsFromDayRows(dayRows);
  const dayKey = dayKeyHint || extracted.dayKey || "";
  const meta = dayMeta(dayKey);
  const { topic, product, lc, problem: extractedProblem } = extracted;
  const problem = shortProblem(extractedProblem || product) || "your project problem";
  const rotLc = pickRot(LC_ROTATION, meta.dayIdx);
  const pattern = lc.pattern || rotLc.pattern || clip(topic, 40) || "problem solving";
  const learnPart = clip(topic, 55) || pattern;
  const next = nextSpeakDay(dayKey);
  const hwCtx = {
    problem,
    topic: learnPart,
    build: product,
    dow: meta.dow,
    week: meta.week,
    dayIdx: meta.dayIdx,
  };

  const useDayLc = Boolean(lc.lc && lc.name);
  const lcNum = useDayLc ? lc.lc : rotLc.lc;
  const lcName = useDayLc ? lc.name : rotLc.name;
  const lcUrl = lc.url || `https://leetcode.com/problems/${rotLc.slug}/`;

  const nightStory = [
    `Problem (short): ${problem}`,
    `Learned today: ${learnPart} · pattern ${pattern} · ${meta.dayName} Week ${meta.week}.`,
    `Connect: Problem → Learn (${learnPart}) → Design → Project (${product}) → Agent Workbench. OKRion = LinkedIn / cert, not the workbench.`,
    `Purpose tonight (~45–70 min): continue THIS day's chain → ${next.dayName} Speak.`,
    `Tasks (max 3): ${meta.dow === 1 ? "Exercism" : "LeetCode"} → ${meta.dow === 2 || meta.dow === 4 ? "OKRion (LinkedIn / cert)" : "SNS Agent Workbench on your project"} → tomorrow's Speak case.`,
  ].join("\n");

  const codingTask = meta.dow === 1
    ? [
        `Exercism`,
        `Week ${meta.week} ${meta.dayName} — ${pattern} drill`,
        `Your product (in short): ${product}`,
        `Link: https://exercism.org/tracks/javascript/exercises`,
        `Do this: Open a beginner ${pattern} exercise. Pass tests. Write 1 line: which skill helps “${learnPart}” on “${product}”.`,
        `Deliverable: passing exercise + 1-line note in notes/exercism-w${meta.week}-d${meta.dayIdx + 1}.md.`,
      ].join("\n")
    : [
        `LeetCode`,
        `Purpose: Week ${meta.week} ${meta.dayName} — continue “${learnPart}” / pattern “${pattern}” on YOUR project.`,
        `Problem: #${lcNum} — ${lcName} (Easy)`,
        `Link: ${lcUrl}`,
        useDayLc ? `Builds on Coding Practice #${lc.lc}${lc.name ? ` — ${lc.name}` : ""}` : null,
        `Project: ${product}`,
        `Do: solve (or log attempt if stuck >25 min). This night's problem is #${lcNum}, not a repeat of another day.`,
        `Apply: Write 3 short bullets: where “${pattern}” / “${learnPart}” fits “${problem}”, which file next, one test input.`,
        `Deliverable: Accepted/logged solution + Apply note (pattern → product).`,
      ].filter(Boolean).join("\n");

  const sideTask = (meta.dow === 2 || meta.dow === 4)
    ? okrionHomeworkBlock(hwCtx)
    : agentHomeworkBlock(hwCtx);

  const csePick = pickRot(CASE_ROTATION, meta.dayIdx);
  const cse = [
    `Case study`,
    `For ${next.dayName} Speak & Solve — Week ${next.week}`,
    `Case: ${csePick.title} [${csePick.theme}]`,
    `What happened: ${csePick.what}`,
    `Problem (short): ${problem}`,
    `Class today: ${learnPart}`,
    `Your product (in short): ${product}`,
    `Do this (easy):`,
    `  1) Skim this case (5–8 min) — it is NOT the same case as other nights.`,
    `  2) Write 3 short lines: what happened / why it matters / 1 lesson for “${learnPart}” on YOUR problem.`,
    `  3) Apply: name ONE change you will make to "${clip(problem, 55)}" (feature, user, or risk).`,
    `Tomorrow: In Speak & Solve, say that 1 lesson + the product change.`,
    `Deliverable: homework-case-w${next.week}-${next.dayName.toLowerCase()}.md with this case title + 3 insights + Apply line.`,
  ].join("\n");

  return [nightStory, codingTask, sideTask, cse].join("\n\n");
}

function fillChunk(chunk) {
  const dayKey = chunk[0]?.[0];
  const holiday = chunk.some(isHolidayRow);
  if (!dayKey || holiday) return chunk;
  const work = chunk.filter((r) => !isHomeworkActivity(r[2]) && !/^tonight$/i.test(String(r[1] || "")));
  const body = buildTonightHomework(work.length ? work : chunk, dayKey);
  const hwIdx = chunk.findIndex((r) => isHomeworkActivity(r[2]) || /^tonight$/i.test(String(r[1] || "")));
  if (hwIdx < 0) {
    return [...chunk, [dayKey, "Tonight", "Tonight's Homework", body]];
  }
  if (!looksLikeOriginalHomework(textOf(chunk[hwIdx])) || isRepeatedTemplate(textOf(chunk[hwIdx]))) {
    const next = chunk.slice();
    const row = next[hwIdx];
    next[hwIdx] = [row[0], row[1] || "Tonight", row[2] || "Tonight's Homework", body];
    return next;
  }
  return chunk;
}

export function withHomeworkRows(rows = []) {
  const list = normalizePlanRows(rows);
  if (!list.length) return list;
  const order = [];
  const groups = new Map();
  list.forEach((r) => {
    const k = String(r[0] || "");
    if (!groups.has(k)) {
      groups.set(k, []);
      order.push(k);
    }
    groups.get(k).push(r);
  });
  const out = [];
  order.forEach((k) => out.push(...fillChunk(groups.get(k))));
  return out;
}

export function homeworkRowsOnly(rows = []) {
  return withHomeworkRows(rows).filter(
    (r) => isHomeworkActivity(r[2]) || /^tonight$/i.test(String(r[1] || ""))
  );
}

export function homeworkPlanFromDaily(daily) {
  const hwRows = homeworkRowsOnly(daily?.rows || []);
  if (!hwRows.length) return null;
  return { module: "homework", title: "Tonight's Homework", rows: hwRows };
}
